import { forwardRef, type ReactElement } from 'react';

import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { Platform } from '../../platform';
import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { KeybindChip } from '../KeybindChip/KeybindChip';
import { Tooltip } from '../Tooltip';
import { BevelHighlight } from './BevelHighlight';
import { BevelInsetWrapper } from './BevelInsetWrapper';
import { pressableDisabled } from './Button.disabled';
import { ICON_SIZE_FOR, resolveButtonIconColor } from './Button.icons';
import { NATIVE_SHADOW_FOR } from './Button.shadows';
import type { ButtonProps } from './Button.types';
import { buttonTextVariants, buttonVariants } from './Button.variants';
import { ButtonGlass } from './ButtonGlass';
import { Spinner } from './Spinner';
import { useButtonAnimation } from './useButtonAnimation';
import { useScaledButtonRadii } from './useScaledButtonRadii';

const IS_WEB = Platform.OS === 'web';

// Native pressed-state scale — mirror of the web CVA's
// `web:enabled:active:scale-[0.97]` (Button.variants.ts). Applied via the
// Pressable primitive's `pressScale` (RN-core Animated), since NativeWind
// can't express the CSS transition on native. No-op on web.
const PRESS_SCALE = 0.97;

// Detached ref shape — see Pressable.tsx for the rationale (pnpm hoists
// multiple @types/react copies; React's `Ref<T>` becomes nominally distinct
// across consumer apps even when versions match).
type AnyRef<T> = ((node: T | null) => void) | { current: T | null } | null;

/**
 * Cross-platform Button. Renders identically on web and React Native.
 *
 * @example
 * ```tsx
 * import { Button } from '@flashka/shared/ui';
 * import { Trash2, ChevronRight, X } from '@flashka/shared/ui/icons';
 *
 * // Text only
 * <Button variant="primary" size="md" onPress={save}>Save</Button>
 *
 * // Icon left / right — color, size, dark mode all automatic
 * <Button variant="alert" leftIcon={Trash2} onPress={remove}>Delete</Button>
 * <Button variant="secondary" rightIcon={ChevronRight} onPress={next}>Next</Button>
 *
 * // Icon only — square box, accessibilityLabel required
 * <Button variant="primary" icon={X} accessibilityLabel="Close" onPress={close} />
 *
 * // Custom icon: any component that accepts `{color, size}`, or one written
 * // with @flashka/shared/ui/svg primitives (same source on both platforms).
 * <Button variant="primary" leftIcon={MyHeartIcon}>Favorite</Button>
 *
 * // Non-icon decoration (badge, avatar) — escape hatch via leftSlot/rightSlot
 * <Button variant="secondary" leftSlot={<UnreadDot />}>Inbox</Button>
 *
 * // Keyboard-shortcut keycap (web-only; replaces any trailing icon/slot)
 * <Button variant="primary" keybinding="K" onPress={openSearch}>Search</Button>
 * ```
 *
 * Disabled uses tone-shift (lighter shade per variant), NOT opacity — per
 * Figma's "Disabled button (no opacity)". The CVA's disabled compounds
 * apply `!opacity-100` to override the Pressable primitives' built-in
 * opacity-50 fallback on both platforms.
 */
function ButtonInner(
  {
    children,
    onPress,
    disabled = false,
    loading,
    leftIcon: LeftIcon,
    rightIcon: RightIcon,
    icon: Icon,
    iconColor: iconColorOverride,
    iconClassName,
    iconSize: iconSizeOverride,
    leftSlot,
    rightSlot,
    keybinding,
    variant,
    size,
    fullWidth = false,
    glass = true,
    className,
    textClassName,
    accessibilityLabel,
    testID,
    webProps,
    nativeProps,
    bevelRadius,
    bevelHighlight,
    tooltipProps,
    onHoverIn,
    onHoverOut,
    haptic = true,
    animationDuration,
    animationEasing,
    animationBezier,
    disableAnimation,
    // Slot-style consumers (Radix `Trigger asChild`, react-router `<Link
    // asChild>`, auth buttons) clone the Button element and attach
    // extra props at the top level — onClick / onPointerDown / aria-* /
    // data-state — and a ref for trigger positioning. Funnel them down to
    // Pressable (which already supports this pattern via its own
    // `...injected` spread) so the popover/dropdown/tooltip actually opens.
    ...injected
  }: ButtonProps & Record<string, unknown>,
  ref: AnyRef<HTMLElement>,
) {
  const { isDark } = useTheme();
  const resolvedVariant = variant ?? 'primary';
  const resolvedSize = size ?? 'm';
  const iconOnly = Icon !== undefined;
  // Keybind chip is WEB-ONLY by product decision — mobile has no
  // keyboard-shortcut surface and doesn't bundle the Chivo Mono face, so
  // the prop is a silent no-op on native (padding included).
  const hasKeybind = IS_WEB && keybinding != null && !iconOnly;

  // Scaled px radii — derived from `button-radii.json` × runtime responsive
  // factor. On web the factor is 1 (CSS rem handles browser zoom + OS text-
  // size); on native it tracks `useWindowDimensions` + `PixelRatio.getFontScale`
  // with iPad ramp and accessibility caps (see `useResponsiveRem.native.ts`).
  const scaled = useScaledButtonRadii(resolvedSize);
  const responsiveScale = useResponsiveScale();

  // Press / state animations. Platform-split hook: web emits CSS-class
  // outputs + optional inline timing overrides; native emits a single
  // class-driven bevel fade. `disableAnimation` short-circuits to instant
  // on web (no-op on native — see `useButtonAnimation.native.ts`).
  const anim = useButtonAnimation({
    disabled,
    loading,
    animationDuration,
    animationEasing,
    animationBezier,
    disableAnimation,
  });

  const textClass = cn(
    buttonTextVariants({ variant: resolvedVariant, size: resolvedSize, disabled }),
    textClassName,
  );
  // Also paint the variant text COLOUR on the Pressable container so icons
  // (rendered as siblings of the inner <Text>) can pick it up via CSS
  // `currentColor` inheritance. Without this the icon would resolve its
  // colour from whatever ancestor sets `text-*`, which on the storybook
  // dark stories is the section wrapper — and Lucide's SVG would then
  // ignore the per-variant tint entirely. Re-using `textClass` is harmless
  // (font-size / font-weight / line-height don't affect SVGs and the
  // inner Text re-applies its own values, which cascade wins anyway).
  const containerClass = cn(
    buttonVariants({
      variant: resolvedVariant,
      size: resolvedSize,
      iconOnly,
      disabled,
      fullWidth,
      // Symmetric right padding only when the chip is the sole trailing
      // visual AND there's no leading icon/slot (Figma: Icon=Yes keeps the
      // default asymmetric padding). Static across `loading` so the button
      // doesn't shift width while the chip is temporarily hidden.
      keybindPad: hasKeybind && !LeftIcon && !leftSlot,
    }),
    textClass,
    loading && !disabled && '!opacity-70',
    anim.containerClassName,
    className,
  );

  // On web, pass `currentColor` so the SVG inherits its colour from the
  // Pressable's CSS `text-*` class above. That makes dark-mode handling
  // CSS-driven — Tailwind's `dark:` modifier respects nested `.dark`
  // ancestors (e.g. storybook's section-scoped dark wrappers), whereas
  // our JS `useTheme()` only reads the `dark` class on `<html>` and
  // would resolve to the light hex inside a section-scoped dark story.
  //
  // On native we keep the explicit resolved hex — `currentColor` is a
  // CSS construct that react-native-svg doesn't honour, and NativeWind
  // bundles dark variants without nested-ancestor scoping anyway.
  const iconColor =
    iconColorOverride ??
    (IS_WEB ? 'currentColor' : resolveButtonIconColor(resolvedVariant, disabled, isDark));
  // Icon px scale with the responsive rem factor (1 on web) — the label text
  // and paddings already scale through rem-based classes, so an unscaled icon
  // reads too small next to them on tablets / large font scales.
  const iconSize =
    Math.round(
      (iconSizeOverride ?? ICON_SIZE_FOR[resolvedSize]) * responsiveScale * 1000,
    ) / 1000;

  // Filled variants get the Figma "Inner-Button" inset-shadow effect —
  // `inset 0 2px 2px 0 rgba(245, 243, 255, 0.30)` — rendered as a real
  // SVG linear-gradient overlay (BevelHighlight) so it works on both web
  // and native with the same soft fade. Wrapped in an `overflow-hidden`
  // View pinned to the Pressable's outer radius so the gradient is
  // clipped to the rounded button shape. The wrapper hides on press via
  // `group-active:hidden` (the Pressable carries `group`), matching the
  // Figma pressed state which drops the inner shadow. Tertiary +
  // secondary have no inner shadow in Figma — skipped.
  //
  // Disabled is ALSO skipped: `!opacity-30` washes the saturated bg out
  // toward white, and the near-white highlight gradient then reads as a
  // noisy outline on top of the pale surface. Dropping the SVG in the
  // disabled state matches what the eye expects (flat pale chip, no
  // pseudo-3D highlight) and avoids that artifact entirely.
  const hasInsetHighlight =
    !disabled &&
    (resolvedVariant === 'primary' ||
      resolvedVariant === 'success' ||
      resolvedVariant === 'alert');
  // The wrapper sits flush against the button's border (inset-0), so the
  // visible inner curve must match the chip's actual inner-edge radius —
  // `scaled.inner` exactly on native (the SVG highlight strokes to it).
  // Web inherits the button's own `border-radius` instead, so a consumer's
  // `rounded-full` is traced too (see `BevelInsetWrapper.tsx`).
  //
  // `bevelRadius` lets native consumers override this when they ship
  // non-default border widths or outer corners (e.g. SrButton's asymmetric
  // 1/2px and 1.5/4px bevels paired with custom rounded-[20/21/22] outer
  // corners). Without it the highlight follows the size default and cuts
  // inside the chip's actual rounded corners.
  const insetRadius = bevelRadius ?? scaled.inner;
  // The bevel wrapper is a platform-split primitive (`BevelInsetWrapper.tsx`
  // for web, `.native.tsx` for native). The animation hook hands back a
  // ready-to-spread slice — web fills `animationClassName` +
  // `webAnimationStyle` (CSS transition); native fills `nativeAnimationStyle`
  // with the reanimated opacity. The split at the file level guarantees the
  // reanimated style never reaches a web prop on native (spreading it into
  // a plain object enumerates `viewDescriptors` and throws "trying to pass
  // an animated style to a non-animated component" at runtime).
  const insetHighlight = hasInsetHighlight ? (
    <BevelInsetWrapper
      size={resolvedSize}
      radius={insetRadius}
      {...anim.bevelWrapperProps}>
      <BevelHighlight
        radius={insetRadius}
        glowColor={bevelHighlight?.color}
        glowOffsetY={bevelHighlight?.offsetY}
        glowBlur={bevelHighlight?.blur}
        webInsetShadow={bevelHighlight?.webInsetShadow}
        webInsetShadowHover={bevelHighlight?.webInsetShadowHover}
        bottomColor={bevelHighlight?.bottomColor}
        bottomOffsetY={bevelHighlight?.bottomOffsetY}
        bottomBlur={bevelHighlight?.bottomBlur}
      />
    </BevelInsetWrapper>
  ) : null;

  // Glass backdrop for the glass variants (secondary / tertiary always-on;
  // ghost only while pressed). Web renders glass via the CVA's
  // `web:backdrop-blur-*`, so ButtonGlass is a no-op there; on iOS it drops
  // a real BlurView behind the content (Android returns null → translucent fill
  // only). Skipped when disabled (the `!opacity-30` wash makes blur read as
  // noise). Ghost passes `interactionOnly` so its BlurView stays hidden at rest
  // and fades in on press (there's no hover/focus for touch).
  const isGlassVariant =
    resolvedVariant === 'secondary' ||
    resolvedVariant === 'tertiary' ||
    resolvedVariant === 'ghost';
  const glassBackground =
    !disabled && glass && isGlassVariant ? (
      <ButtonGlass
        size={resolvedSize}
        radius={insetRadius}
        interactionOnly={resolvedVariant === 'ghost'}
      />
    ) : null;

  // Merge the variant-specific native shadow + the scaled outer-radius
  // override into the caller's `nativeProps`. Web ignores these style fields
  // (the Pressable primitive's web file swallows `nativeProps`); on native
  // the RN shadow API renders the violet/green/red glow per Figma
  // "Shadow-Button-*", and the radius override applies the runtime-scaled
  // uniform outer corner radius (`outerTop` for all four corners — the
  // bevel border was dropped, so there's no asymmetry to compensate for).
  // RN's `style` prop accepts an array — later entries override earlier —
  // so the caller's custom style still wins where keys overlap.
  const nativeShadow = NATIVE_SHADOW_FOR[resolvedVariant];
  const radiusOverride = {
    borderTopLeftRadius: scaled.outerTop,
    borderTopRightRadius: scaled.outerTop,
    borderBottomLeftRadius: scaled.outerTop,
    borderBottomRightRadius: scaled.outerTop,
  };
  const mergedNativeProps = {
    ...(nativeProps ?? {}),
    style: [
      nativeShadow,
      radiusOverride,
      (nativeProps as { style?: unknown } | undefined)?.style,
    ].filter(Boolean),
  };

  // Merge the hook's web `containerStyle` (transition-duration / timing-
  // function overrides when callers customise animationDuration /
  // animationEasing, or `transition: none` when disableAnimation) into the
  // caller's webProps.style. Pressable's web file spreads webProps onto the
  // underlying <button>, so the inline `transition: …` lands on the element
  // and overrides the Tailwind `transition-[…] duration-150 ease-out` base.
  const mergedWebProps = anim.containerStyle
    ? {
        ...(webProps ?? {}),
        style: { ...(webProps?.style ?? {}), ...anim.containerStyle },
      }
    : webProps;

  // Icon-only branch: just the icon (or spinner while loading). No text
  // wrapper, no left/right slots — those would break the square layout.
  //
  // `{...injected}` carries non-standard props slot-style consumers attach
  // (Radix `Trigger asChild`, react-router `Link asChild`, auth
  // buttons — onClick / onPointerDown / aria-* / data-state, plus any
  // onPressIn/onPressOut consumers wire) down to the Pressable, which
  // composes them with its own handlers. Top-level handlers below
  // (`onPress`, `onHoverIn`, …) override anything in `injected` since
  // they're spread last.
  if (iconOnly) {
    return withTooltip(
      <Pressable
        {...injected}
        ref={ref}
        className={containerClass}
        disabled={pressableDisabled(disabled, loading)}
        onPress={onPress}
        onHoverIn={onHoverIn}
        onHoverOut={onHoverOut}
        haptic={haptic}
        pressScale={disableAnimation ? undefined : PRESS_SCALE}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        testID={testID}
        webProps={mergedWebProps}
        nativeProps={mergedNativeProps}>
        {glassBackground}
        {insetHighlight}
        {loading ? (
          <Spinner color={iconColor} size={iconSize} />
        ) : Icon ? (
          <Icon color={iconColor} size={iconSize} className={iconClassName} />
        ) : null}
      </Pressable>,
      { tooltipProps, disabled },
    );
  }

  // Regular branch: leftIcon/leftSlot, label, rightIcon/rightSlot. `leftIcon`
  // wins over `leftSlot` (icon path is the documented default). Spinner
  // replaces the leading visual during loading; trailing visual is hidden
  // during loading to keep the layout stable.
  //
  // Wrap text-like children (string, number, or an array of those — what
  // JSX produces for mixed-content like `Button {size}`) in Text so the
  // variant typography classes actually apply. Pass JSX through untouched.
  //
  // The label is SHRINKABLE and single-line. A chip has a fixed height per
  // size (`h-12` etc.), so a label that outgrows its box cannot wrap — without
  // `min-w-0 shrink` it keeps its intrinsic width and simply spills past the
  // chip's right edge (seen with "Generate from Document" in the iPad deck's
  // 380pt card pane, where the text ran out of the button and over the pane).
  // With them it stays inside: BOTH platforms tail-ellipsize. Native must NOT
  // use `adjustsFontSizeToFit` here: under Fabric it mis-scales far below its
  // own `minimumFontScale` when the label re-measures inside transformed/
  // animated ancestors or across a variant flip (onboarding chips rendered at
  // ~half size on TestFlight, 2026-09-01), and it re-fits the already-shrunk
  // label on every re-render, so a label inside a component that re-renders
  // often (the recorder's ticking timer / level meter) ends up at a fraction
  // of its size while its box keeps the full width (tiny "Pause" / "Stop" /
  // "Transcribe" on TestFlight) — deterministic ellipsis over sometimes-tiny
  // glyphs.
  const label = isTextLike(children) ? (
    <Text
      className={cn('min-w-0 shrink web:overflow-hidden web:text-ellipsis', textClass)}
      // NATIVE-ONLY, deliberately not the primitive's `numberOfLines` prop:
      // that one also drives web's `-webkit-box` line-clamp, and swapping the
      // label's display box changes how it sits on the chip's baseline. Web
      // gets the plain `overflow-hidden`/`text-ellipsis` pair above instead
      // (the CVA base already sets `whitespace-nowrap`), which is the whole
      // truncation contract there.
      nativeProps={{ numberOfLines: 1 }}>
      {children}
    </Text>
  ) : (
    children
  );

  // An icon beside a label never shrinks: when the chip has to give way, the
  // label ellipsizes (above) — a squeezed glyph only looked broken (a type
  // picker's chevron drew 6px wide).
  const sideIconClass = cn('shrink-0', iconClassName);
  const leading = loading ? (
    <Spinner color={iconColor} size={iconSize} />
  ) : LeftIcon ? (
    <LeftIcon color={iconColor} size={iconSize} className={sideIconClass} />
  ) : (
    leftSlot
  );

  // Keybind chip (Figma "Shortcut", Keybind=Yes). Wins over rightIcon /
  // rightSlot — a button with a keybinding never carries a trailing icon.
  // State-invariant per Figma (identical across hover/pressed/focused;
  // disabled is covered by the button-level !opacity-30), so no state
  // classes. xxs/xs/s share the "Small" chip; m–xxl the "Default" chip.
  const chipSize =
    resolvedSize === 'xxs' || resolvedSize === 'xs' || resolvedSize === 's'
      ? ('sm' as const)
      : ('md' as const);
  const keybindChip = hasKeybind ? (
    <KeybindChip variant={resolvedVariant} chipSize={chipSize}>
      {keybinding}
    </KeybindChip>
  ) : null;

  const trailing = loading
    ? null
    : (keybindChip ??
      (RightIcon ? (
        <RightIcon color={iconColor} size={iconSize} className={sideIconClass} />
      ) : (
        rightSlot
      )));

  return withTooltip(
    <Pressable
      {...injected}
      ref={ref}
      className={containerClass}
      disabled={pressableDisabled(disabled, loading)}
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      haptic={haptic}
      pressScale={disableAnimation ? undefined : PRESS_SCALE}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      testID={testID}
      webProps={mergedWebProps}
      nativeProps={mergedNativeProps}>
      {glassBackground}
      {insetHighlight}
      {leading}
      {label}
      {trailing}
    </Pressable>,
    { tooltipProps, disabled },
  );
}

/**
 * Public Button. `forwardRef` so `asChild` consumers (Radix Popover /
 * Dropdown / Dialog / Tooltip triggers, react-router `<Link>`) can hand the
 * underlying DOM element back to the library for positioning, focus
 * management, and aria-* wiring. Cast through `unknown` to detach from
 * React's `Ref<T>` generic — see `AnyRef` and the analogous pattern in
 * `Pressable.tsx`. Runtime is identical to a plain `forwardRef`.
 */
export const Button = forwardRef(
  ButtonInner as never,
) as unknown as React.ForwardRefExoticComponent<
  ButtonProps & { ref?: AnyRef<HTMLElement> }
>;
(Button as { displayName?: string }).displayName = 'Button';

/**
 * Wrap a Pressable in a Tooltip only when the caller passed `tooltipProps`
 * with a non-empty `content`. Skipped otherwise so plain buttons don't
 * pay for the Radix tree / hover-state plumbing.
 *
 * A disabled Button forces the tooltip off (matches the no-press-target
 * intuition). Consumers can opt in to a disabled-but-hoverable tooltip
 * by leaving Button enabled and supplying their own `disabled` inside
 * `tooltipProps`.
 */
function withTooltip(
  node: ReactElement,
  options: {
    tooltipProps: ButtonProps['tooltipProps'];
    disabled: boolean;
  },
): ReactElement {
  const tp = options.tooltipProps;
  if (!tp || tp.content == null || tp.content === false) return node;
  return (
    <Tooltip {...tp} disabled={options.disabled || tp.disabled}>
      {node}
    </Tooltip>
  );
}

function isTextLike(c: unknown): boolean {
  if (c == null || typeof c === 'boolean') return false;
  if (typeof c === 'string' || typeof c === 'number') return true;
  if (Array.isArray(c)) return c.every(isTextLike);
  return false;
}
